import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import clsx from 'clsx';
import React from 'react';

import type { PaletteHandle } from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, Tooltip } from '@gitbook/spine-react';

import { shortcuts, useKeyboardShortcuts } from '../../../../../../Keyboard';
import { TableStatesContext } from '../../../contexts';
import type { TableRecordKV } from '../../../types';

interface RowOptionsProps {
    editor: EditorContainer;
    tableKey: string;
    record: TableRecordKV;
    attributes: DraggableAttributes;
    listeners: DraggableSyntheticListeners;
    isDragging: boolean;
    paletteHandle: PaletteHandle;
    paletteButtonRef: React.RefObject<HTMLButtonElement>;
    onPressOptions: () => void;
}

export function RowSettings(props: RowOptionsProps) {
    const { editor } = props;

    return (
        <RowSettingsObfuscator>
            {!editor.readOnly ? <RowSettingsButton {...props} /> : null}
        </RowSettingsObfuscator>
    );
}

function RowSettingsButton(props: Omit<RowOptionsProps, 'editor'>) {
    const {
        tableKey,
        record,
        attributes,
        listeners,
        isDragging,
        paletteHandle,
        paletteButtonRef,
        onPressOptions,
    } = props;

    // Check if this row is currently selected
    const tableStatesContext = React.useContext(TableStatesContext);
    const currentTableState = tableStatesContext.get(tableKey);
    const isCurrentRowSelected = currentTableState?.selection.recordId === record.key;

    // Register keyboard shortcut only for the currently selected row
    useKeyboardShortcuts(
        isCurrentRowSelected ? { [shortcuts.TABLE_ROW_OPTIONS]: onPressOptions } : {},
        'editor-selection-collapsed'
    );

    const isOpenedOrDragging = paletteHandle.opened || isDragging;
    return (
        <Tooltip
            label="Click for row options"
            description="Drag to move"
            shortcut={shortcuts.TABLE_ROW_OPTIONS}
            disabled={isDragging}
        >
            <AtomButton
                ref={paletteButtonRef}
                {...attributes}
                {...listeners}
                onPress={onPressOptions}
                tabIndex={-1}
                style={
                    {
                        '--knob-bg-default': 'var(--sp-color-bg-table-drag-button-default)',
                        '--knob-bg-active': 'var(--sp-color-bg-table-drag-button-active)',
                    } as React.CSSProperties
                }
                className={clsx(
                    'group/rowSettings absolute z-20 flex items-center justify-center rounded-[5px] opacity-0 transition-all',
                    // Default styles, a slim 'knob'
                    'h-6 w-[5px] px-0.5',
                    // Entire row is hovered
                    'group-focus-within/tablerow:opacity-100 group-hover/tablerow:opacity-100',
                    // When knob is hovered or focused, knob expands and shows icon within
                    'hover:-right-[6px] hover:bg-[var(--knob-bg-active)] hover:px-1.5',
                    // When knob is selected (incl. onMouseDown)
                    'active:scale-95 active:bg-[var(--knob-bg-active)] active:shadow-focus-ring',
                    isOpenedOrDragging
                        ? '-right-[6px] bg-[var(--knob-bg-active)] px-1.5 opacity-100 shadow-focus-ring'
                        : '-right-[3px] bg-[var(--knob-bg-default)]',
                    isDragging && 'cursor-grabbing',
                    'active:cursor-grab'
                )}
            >
                <Icon.MoreVertical
                    size={14}
                    className={clsx(
                        'shrink-0 text-inverted-standout transition-opacity',
                        isOpenedOrDragging
                            ? 'opacity-100 '
                            : 'opacity-0 delay-100 group-hover/rowSettings:opacity-100'
                    )}
                />
            </AtomButton>
        </Tooltip>
    );
}

/**
 * We position the row controls button within a 'fake' row cell at the start of each row. This allows us to 'overflow' the button on the left-side, while still allowing the table to remain scrollable.
 * Added benefit: the button is sticky, so as you scroll, the button remains in position.
 */
export function RowSettingsObfuscator({ children }: { children?: React.ReactNode }) {
    return (
        <div
            data-exclude-from-copy
            className={clsx(
                'group/tablerow sticky top-0 bottom-0 left-0 z-10 flex w-2.5 min-w-2.5 shrink-0 items-center justify-center self-stretch bg-base p-0'
            )}
        >
            {children}
        </div>
    );
}
